<template>
  <ViewContainer :title="$t('rev.validation.impact_validation')">
    <LoadingMode v-if="loading" />
    <v-form v-else v-model="isFormValid" ref="validationForm">
      <v-alert
        v-if="hasCanceledRequestStatus"
        border="left"
        class="alert-container py-4"
        colored-border
        dense
        outlined
        dismissible
        color="red"
        icon="mdi-alert-decagram"
      >
        <p class="ma-0 pr-2 body1">
          {{ $t('rev.validation.cancel_alert', { revName }) }}
        </p>
        <template v-slot:close="{ toggle }">
          <v-icon size="25" color="blue-grey" @click="toggle()"
            >mdi-close-circle</v-icon
          >
        </template>
      </v-alert>

      <Button
        plain
        icon="mdi-arrow-left"
        class="mb-4"
        :text="$t('rev.validation.back_to_validations')"
        @click="goBack()"
      />

      <FinancialValidationHeader
        class="px-8 py-6 mb-7"
        :revType="camelCaseRevType"
        :revId="revId"
        :organizationId="organizationId"
        :rev="rev"
        :validationInProgress="hasValidationRequestInProgress"
        :assignee="assignee"
        :requestedBy="requestedBy"
        :requestedOn="requestedOn"
      />
      <v-row>
        <v-col cols="12" sm="4">
          <v-card
            class="px-8 py-6 mb-6"
            data-tourid="validationView-card-estimatedMetrics"
          >
            <span class="subtitle-2 blue-grey--text text--darken-3">
              {{ $t('rev.validation.estimated_impact') }}:
            </span>

            <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
              {{ $t('rev_cycle.review.impact_breakdown') }}
            </p>
            <Metrics
              addButtonTourId="revImpacts-button-addImpact"
              testId="estimated-impact-metrics"
              isImpact
              :initialMetrics="estimatedImpactMetrics"
              :metrics="impactMetrics"
              :revCurrency="revCurrency"
              :organizationCurrency="organizationCurrency"
              :exchangeRate="exchangeRate"
            />

            <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
              {{ $t('rev_cycle.review.investment') }}
            </p>
            <Metrics
              testId="estimated-investment-metrics"
              addButtonTourId="revImpacts-button-addInvestment"
              :initialMetrics="estimatedInvestmentMetrics"
              :metrics="investmentMetrics"
              :revCurrency="revCurrency"
              :organizationCurrency="organizationCurrency"
              :exchangeRate="exchangeRate"
            />

            <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
              {{ $t('common.other_one') }}
            </p>

            <TextQuestion
              data-private
              data-testid="validation-estimated-calculate-benefits"
              class="my-4"
              rows="6"
              :tourId="'estimated-textArea-calculate-benefits'"
              :value="estimatedBenefits"
              :question="
                $t('rev_cycle.review.how_did_you_estimate_the_benefits')
              "
              :maxLength="$constants.rev.benefitEstimationMaxLength"
              @input="$emit('update', 'estimationExplanation', $event)"
            />
          </v-card>
        </v-col>

        <v-col
          cols="12"
          sm="8"
          data-tourid="validationView-form-validatedMetrics"
        >
          <v-card class="px-8 py-6 mb-6">
            <span class="subtitle-2 blue-grey--text text--darken-3">
              {{ $t('rev.validation.impact_validated') }}:
            </span>

            <v-row
              class="mt-6"
              data-tourid="validationView-form-totalImpact"
              no-gutters
              :class="{ 'mb-4': !isEditMode }"
            >
              <v-col cols="12" sm="6" class="pr-4">
                <p
                  class="subtitle-2 mb-4 font-weight-500 blue-grey--text text--darken-1"
                >
                  {{ $t('rev.validation.total_impact_of_rev') }}*
                </p>
                <MoneyInput
                  v-if="isEditMode"
                  allowDecimals
                  required
                  data-testid="total-impact-field"
                  :label="$t('common.amount')"
                  :currencyCode="revCurrency"
                  :currencySymbol="currencySymbol"
                  :value="totalImpact"
                  @onInput="newVal => (totalImpact = newVal)"
                />
                <span
                  v-else
                  class="mb-4 font-weight-bold blue-grey--text text--darken-3 font-size-20"
                >
                  {{ totalImpactText }}</span
                >
              </v-col>

              <v-col cols="12" sm="6">
                <p
                  class="subtitle-2 mb-4 font-weight-500 blue-grey--text text--darken-1"
                >
                  {{ $t('common.time_frame_impact') }}*
                </p>
                <v-chip
                  v-if="!isEditMode && !startDate && !endDate"
                  small
                  disabled
                  >{{ $t('common.in_progress_status') }}</v-chip
                >
                <div v-else class="d-flex align-end">
                  <DatePicker
                    data-testid="validation-start-date-picker"
                    required
                    :allowed-dates="startDateAllowed"
                    :editMode="isEditMode"
                    :inputClass="'mr-1'"
                    :label="$t('common.date.start_date')"
                    :value="startDate"
                    @change="handleDateChange('changeStartDate', $event)"
                  />
                  <span
                    v-if="!isEditMode"
                    class="font-weight-400 blue-grey--text text--darken-3 font-size-16 mx-1"
                  >
                    {{ $t('common.to') }}
                  </span>
                  <DatePicker
                    data-testid="validation-end-date-picker"
                    required
                    :allowed-dates="endDateAllowed"
                    :editMode="isEditMode"
                    :inputClass="'ml-1'"
                    :label="$t('common.date.end_date')"
                    :value="endDate"
                    @change="handleDateChange('changeEndDate', $event)"
                  />
                </div>
              </v-col>
            </v-row>

            <div data-tourid="validationView-form-metricsBreakdown">
              <p class="mb-4 mt-0 subtitle-2 blue-grey--text text--darken-1">
                {{ $t('rev_cycle.review.impact_breakdown') }}
              </p>
              <span v-if="!isEditMode && !selectedImpacts.length">-</span>
              <Metrics
                v-else
                addButtonTourId="revImpacts-button-addImpact"
                testId="validation-impact-metric-card"
                isImpact
                requireFillMetric
                :editMode="isEditMode"
                :initialMetrics="selectedImpacts"
                :metrics="impactMetrics"
                :revCurrency="revCurrency"
                :organizationCurrency="organizationCurrency"
                :exchangeRate="exchangeRate"
                @change="handleImpactMetricsChange"
                @add="
                  trackAddImpactMetric({
                    view: 'validation',
                    revId,
                    revType: camelCaseRevType,
                    metric: $event,
                  })
                "
                @delete="
                  trackDeleteImpactMetric({
                    view: 'validation',
                    revId,
                    revType: camelCaseRevType,
                    metric: $event,
                  })
                "
              />

              <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
                {{ $t('rev_cycle.review.investment') }}
              </p>
              <span v-if="!isEditMode && !selectedInvestments.length">-</span>
              <Metrics
                v-else
                testId="validation-investment-metric-card"
                addButtonTourId="revImpacts-button-addInvestment"
                requireFillMetric
                :editMode="isEditMode"
                :initialMetrics="selectedInvestments"
                :metrics="investmentMetrics"
                :revCurrency="revCurrency"
                :organizationCurrency="organizationCurrency"
                :exchangeRate="exchangeRate"
                @change="handleInvestmentMetricsChange"
                @add="
                  trackAddInvestment({
                    view: 'validation',
                    revId,
                    revType: camelCaseRevType,
                    investment: $event,
                  })
                "
                @delete="
                  trackDeleteInvestment({
                    view: 'validation',
                    revId: rev.id,
                    revType: camelCaseRevType,
                    investment: $event,
                  })
                "
              />

              <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
                {{ $t('common.other_one') }}
              </p>

              <TextQuestion
                data-private
                data-testid="validation-estimated-calculate-benefits"
                class="my-4"
                rows="6"
                :isEditMode="isEditMode"
                :tourId="'estimated-textArea-calculate-benefits'"
                :value="estimationExplanation"
                :question="
                  $t('rev_cycle.review.how_did_you_estimate_the_benefits')
                "
                :maxLength="$constants.rev.benefitEstimationMaxLength"
                @input="estimationExplanation = $event"
                @focusOut="
                  trackBenefitsEstimation({
                    view: 'validation',
                    revId: rev.id,
                    revType: camelCaseRevType,
                    comment: estimationExplanation,
                  })
                "
              />
            </div>

            <div
              v-if="isEditMode"
              class="mt-4 py-4 d-flex sticky-options white"
            >
              <Button
                class="ml-0"
                color="danger"
                plain
                v-if="hasValidationRequestInProgress"
                :text="$t('rev.validation.reject_request')"
                @click="showRejectedRequestModal = true"
              />
              <div class="mr-0 ml-auto">
                <Button
                  color="blue"
                  plain
                  v-if="isFinancialValidated"
                  :text="$t('common.button.cancel')"
                  @click="handleCancelEdit"
                />
                <Button
                  outlined
                  v-if="!isFinancialValidated"
                  color="primary"
                  :disabled="loading"
                  :loading="loading"
                  :text="$t('rev.validation.save_validate_later')"
                  @click="handleSaveValues(false)"
                />
                <Button
                  color="primary"
                  class="ml-2"
                  data-tourid="validationView-button-validateRev"
                  :disabled="loading"
                  :loading="loading"
                  :text="submitText"
                  @click="handleSaveValues(!isFinancialValidated, true)"
                />
              </div>
            </div>
            <div
              v-else-if="isFinancialValidated || !isEditMode"
              class="mt-4 py-4 text-end sticky-options white"
            >
              <div class="d-flex align-end justify-end">
                <Button
                  v-if="isFinancialValidated"
                  color="primary"
                  class="me-2"
                  outlined
                  @click="showDeleteValidationModal = true"
                  :text="$t('rev.validation.delete_validation')"
                />
                <Button
                  v-if="!isEditMode"
                  color="primary"
                  @click="isEditMode = true"
                  :text="$t('common.button.edit')"
                />
                <Button
                  v-if="!isFinancialValidated"
                  color="primary"
                  class="ml-2"
                  data-tourid="validationView-button-validateRev"
                  :disabled="loading"
                  :loading="loading"
                  :text="submitText"
                  @click="handleSaveValues(!isFinancialValidated, true)"
                />
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
    <SuccessModal
      :show="showSuccessModal"
      @close="handleCloseSuccess"
      @goToValidationsView="goToValidationsAfterValidate"
    />
    <RejectValidationModal
      :show="showRejectedRequestModal"
      :loading="loading"
      @cancel="showRejectedRequestModal = false"
      @delete="handleCancelRequest"
    />
    <DeleteConfirmationModal
      :show="showDeleteValidationModal"
      :maxWidth="454"
      :title="$t('rev.validation.delete_validation')"
      :text="$t('rev.validation.are_you_sure_you_want_to_delete_validation')"
      :confirmText="$t('common.button.validation_eliminate')"
      :isLoading="loading"
      @cancel="showDeleteValidationModal = false"
      @delete="handleDeleteValidation"
    />
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin'
import revMixin from '@/mixins/revMixin'

import trackerMixin from '@/mixins/tracking/trackerMixin'

import api from '@/api'
import revSerializer from '@/lib/revSerializer.js'
import currencies from '@/locales/currencies.json'

import SuccessModal from './SuccessModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import LoadingMode from './LoadingMode.vue'
import RejectValidationModal from './RejectValidationDialog.vue'
import FinancialValidationHeader from './FinancialValidationHeader.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import Button from '@/components/UI/Button.vue'
import Metrics from '@/components/Rev/Review/Metrics.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import DatePicker from '@/components/Core/DatePicker.vue'
import MoneyInput from '@/components/Rev/Review/MoneyInput.vue'

const REVTYPEMODEL = {
  improvementIdea: 'improvement-ideas',
  problemSolving: 'problem-solving',
  quickAction: 'quick-actions',
}

function normalizeRevType(revType) {
  if (revType === 'rev') return 'improvement-idea'
  return revType
}

export default {
  name: 'FinancialValidationView',

  mixins: [mixin, revMixin, trackerMixin],

  components: {
    ViewContainer,
    LoadingMode,
    FinancialValidationHeader,
    Metrics,
    TextQuestion,
    SuccessModal,
    RejectValidationModal,
    DeleteConfirmationModal,
    Button,
    DatePicker,
    MoneyInput,
  },

  data() {
    return {
      isFormValid: false,
      revId: this.$route.params.revId,
      revType: normalizeRevType(this.$route.params.revType),
      rev: { name: '' },
      isEditMode: false,
      loading: false,
      showDeleteValidationModal: false,
      showSuccessModal: false,
      showRejectedRequestModal: false,
      REVTYPEMODEL,
      totalImpact: null,
      startDate: null,
      endDate: null,
      impactMetrics: [],
      investmentMetrics: this.$constants.investments,
      selectedImpacts: [],
      selectedInvestments: [],
      estimationExplanation: '',
      prevRoute: null,
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  watch: {
    originalValidatedReview(newValue) {
      this.$emit('change', {
        originalValidatedReview: newValue,
      })
    },
    newValidatedReview(newValue) {
      this.$emit('change', { newValidatedReview: newValue })
    },
  },

  async mounted() {
    try {
      this.loading = true
      await Promise.all([this.getRev(), this.getMetrics()])
    } catch (err) {
      this.showError(err)
    } finally {
      this.loading = false
    }

    this.isEditMode = !this.isFinancialValidated
  },

  computed: {
    ...mapGetters(['organizationId', 'account', 'language']),

    validationId() {
      return (
        this.rev?.validations__financial__id ??
        this.rev?.validations?.financial?.id ??
        this.rev?.validation?.financialValidationId
      )
    },

    exchangeRate() {
      return this.rev ? this.getExchangeRate(this.rev) : 0
    },

    isFinancialValidated() {
      return this.rev ? this.isFinanciallyValidated(this.rev) : false
    },

    showRevenue() {
      return this.camelCaseRevType !== this.$constants.revTypes.quickAction
    },

    requestedBy() {
      return this.validationRequestInProgress?.createdBy ?? null
    },

    requestedOn() {
      return this.validationRequestInProgress?.createdAt ?? null
    },

    assignee() {
      return this.validationRequestInProgress?.assignees?.[0] ?? null
    },

    validationRequestInProgress() {
      return (
        this.rev?.validationRequest?.find?.(
          request =>
            request.status ===
            this.$constants.validationRequestStatus.inProgress
        ) ?? null
      )
    },

    hasValidationRequestInProgress() {
      return Boolean(this.validationRequestInProgress?.id)
    },

    camelCaseRevType() {
      return _.camelCase(this.revType)
    },

    revApiFile() {
      if (
        this.camelCaseRevType === this.$constants.revTypes.quickAction ||
        this.camelCaseRevType === this.$constants.revTypes.problemSolving
      )
        return this.camelCaseRevType.toLowerCase()

      return 'rev'
    },

    revCurrency() {
      return this.rev ? this.getRevCurrency(this.rev) : ''
    },

    organizationCurrency() {
      return this.rev ? this.getOrganizationCurrency(this.rev) : ''
    },

    revName() {
      return this.rev?.name ?? null
    },

    owner() {
      return {
        ...this.rev?.team?.owner,
        name: this.getFullName(this.rev?.team?.owner),
      }
    },

    coach() {
      return {
        ...this.rev?.team?.coach,
        name: this.getFullName(this.rev?.team?.coach),
      }
    },

    modelName() {
      const parsedRevType = _.camelCase(this.revType)
      if (parsedRevType === this.$constants.revTypes.improvementIdea) {
        return this.$constants.revModelTypes.improvementIdea
      }
      if (parsedRevType === this.$constants.revTypes.quickAction) {
        return this.$constants.revModelTypes.quickAction
      }
      return this.$constants.revModelTypes.problemSolving
    },

    currencySymbol() {
      return currencies?.[this.revCurrency]?.symbol_native ?? ''
    },

    totalImpactText() {
      const amount = parseFloat(this.totalImpact).toLocaleString(
        navigator.language,
        {
          maximumFractionDigits: 2,
        }
      )
      return this.totalImpact
        ? `${this.currencySymbol} ${amount} ${this.revCurrency}`
        : '-'
    },

    estimatedImpactMetrics() {
      return this.rev?.reviewPhase?.impacts ?? []
    },

    estimatedInvestmentMetrics() {
      return this.rev?.reviewPhase?.investments ?? []
    },

    estimatedBenefits() {
      return (
        this.rev?.reviewPhase?.estimationExplanation ??
        this.rev?.review?.estimationComment ??
        ''
      )
    },

    estimatedCategories() {
      return this.rev?.categories ?? []
    },

    submitText() {
      return this.isFinancialValidated
        ? this.$t('common.button.save')
        : this.$t('rev.validation.validate_impact')
    },

    originalValidatedReview() {
      return {
        startDate: this.rev?.impactStartsAt
          ? utils.dates.formatDate(this.rev?.impactStartsAt)
          : null,
        endDate: this.rev?.impactEndsAt
          ? utils.dates.formatDate(this.rev?.impactEndsAt)
          : null,
        totalImpact: this.rev?.validatedReview?.totalImpact
          ? parseFloat(this.rev?.validatedReview?.totalImpact).toFixed(2)
          : null,
        impacts: this.isPartiallySavedOrValidated
          ? this.rev?.validatedReview?.impacts
          : this.rev?.reviewPhase?.impacts,
        investments: this.isPartiallySavedOrValidated
          ? this.rev?.validatedReview?.investments
          : this.rev?.reviewPhase?.investments,
        estimationExplanation: this.isPartiallySavedOrValidated
          ? this.rev?.validatedReview?.estimationExplanation
          : this.estimatedBenefits,
      }
    },

    newValidatedReview() {
      return {
        startDate: this.startDate
          ? utils.dates.formatDate(this.startDate)
          : null,
        endDate: this.endDate ? utils.dates.formatDate(this.endDate) : null,
        totalImpact: parseFloat(this.totalImpact).toFixed(2),
        estimationExplanation: this.estimationExplanation,
        impacts: this.selectedImpacts,
        investments: this.selectedInvestments,
      }
    },

    hasCanceledRequestStatus() {
      const inProgress = this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.inProgress
      )

      const isValidated = this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.completed
      )
      const canceled = this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.canceled
      )

      return Boolean(!isValidated && !inProgress && canceled)
    },

    isPartiallySavedOrValidated() {
      return (
        !!this.rev?.validatedReview?.lastUpdatedBy || this.isFinancialValidated
      )
    },
  },

  methods: {
    ...mapActions({
      setImpactValidation: 'validations/setImpactValidation',
    }),

    handleCloseSuccess() {
      this.getRev()
      this.showSuccessModal = false
      this.isEditMode = false
    },

    handleCancelEdit() {
      this.getRev()
      this.isEditMode = false
    },

    async handleValidation() {
      try {
        this.loading = true
        const revType =
          this.camelCaseRevType === this.$constants.revTypes.improvementIdea
            ? 'rev'
            : this.revType.replace('-', '').toLowerCase() // "revType" is used to determine the api endpoint to use, so a notation like "quickaction" (all in lower case) is required for this to work

        const response = await this.setImpactValidation({
          revId: this.revId,
          organizationId: this.organizationId,
          isValidated: this.isFinancialValidated,
          revType,
          validationId: this.rev?.validations?.financial?.id,
        })

        this.trackValidationValidate({
          revId: this.revId,
          revType: this.camelCaseRevType,
          status: response.status === 201 ? 'success' : 'fail',
          isEdited: false,
        })
        await this.getRev()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
        this.isEditMode = false
        this.showSuccessModal = true
      }
    },

    async handleSaveValues(validateAfterSave = false, reloadRev = true) {
      try {
        if (this.isFinancialValidated || validateAfterSave) {
          this.$refs.validationForm.validate()
          if (!this.isFormValid) {
            return this.showError(
              this.$t('rev.validation.empty_fields_error_message')
            )
          }
        }
        this.loading = true
        const payload = _.omitBy(
          {
            impactStartsAt: this.startDate
              ? utils.dates.getStartOfDay(this.startDate)
              : null,
            impactEndsAt: this.endDate
              ? utils.dates.getEndOfDay(this.endDate)
              : null,
            totalImpact: this.totalImpact,
            estimationExplanation: this.estimationExplanation,
            impacts: this.selectedImpacts,
            investments: this.selectedInvestments,
          },
          _.isNull
        )

        await api[this.revApiFile].savePartialValidation(
          this.organizationId,
          this.revId,
          payload
        )

        this.trackValidationPartialSave({
          revId: this.revId,
          revType: this.camelCaseRevType,
          isFinancialValidated: this.isFinancialValidated,
        })

        if (reloadRev) await this.getRev()

        if (this.isFinancialValidated) {
          this.showSuccess(
            this.$t('rev.validation.validation_updated_successfully')
          )
        } else if (!validateAfterSave) {
          this.showSuccess(this.$t('rev.validation.fields_saved_successfully'))
        }

        if (validateAfterSave) await this.handleValidation()

        this.isEditMode = false
        this.loading = false
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getRev() {
      try {
        this.loading = true

        const response = await api[this.revApiFile].getById(
          this.organizationId,
          this.revId
        )

        this.rev =
          this.camelCaseRevType === this.$constants.revTypes.improvementIdea
            ? revSerializer.serializeImprovementIdeaForLocalState(response.data)
            : response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
        this.setInitialValidationReview()
      }
    },

    setInitialValidationReview() {
      this.startDate = this.rev?.impactStartsAt ?? null
      this.endDate = this.rev?.impactEndsAt ?? null
      this.totalImpact = this.rev?.validatedReview?.totalImpact
        ? parseFloat(this.rev?.validatedReview?.totalImpact).toFixed(2)
        : null
      this.selectedImpacts = this.isPartiallySavedOrValidated
        ? this.rev?.validatedReview?.impacts
        : this.rev?.reviewPhase?.impacts
      this.selectedInvestments = this.isPartiallySavedOrValidated
        ? this.rev?.validatedReview?.investments
        : this.rev?.reviewPhase.investments
      this.estimationExplanation = this.isPartiallySavedOrValidated
        ? this.rev?.validatedReview?.estimationExplanation
        : this.estimatedBenefits
    },

    handleCancelRequest(comment) {
      this.trackValidationRequest({
        action: 'rejectConfirm',
        type: 'buttonClick',
        containComment: Boolean(comment.content),
      })
      this.setCancelRequest(comment)
      this.showRejectedRequestModal = false
    },

    async handleDeleteValidation() {
      try {
        const revType =
          this.camelCaseRevType === this.$constants.revTypes.improvementIdea
            ? 'rev'
            : this.revType.replace('-', '').toLowerCase()
        const payload = {
          revId: this.revId,
          organizationId: this.organizationId,
          isValidated: this.isFinancialValidated,
          revType,
          validationId: this.validationId,
        }
        const response = await this.setImpactValidation(payload)
        this.trackDeleteValidation({
          status: response.status === 200 ? 'success' : 'fail',
        })

        await this.getRev()
        this.showDeleteValidationModal = false
        this.showSuccess(this.$t('rev.validation.rev_validation_deleted'))
        this.isEditMode = true
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async setCancelRequest(comment) {
      try {
        this.onSubmitComment(comment)
        await api.validationRequest.update(
          this.organizationId,
          this.REVTYPEMODEL[this.camelCaseRevType],
          this.revId,
          this.validationRequestInProgress?.id,
          this.$constants.validationRequestStatus.canceled
        )
        await this.getRev()
        this.showSuccess(
          this.$t('rev.request_to_validate_this_rev_has_been_rejected')
        )
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async onSubmitComment(comment) {
      if (!comment.content) return
      try {
        await api.comment.postComment(
          this.organizationId,
          this.modelName,
          this.revId,
          comment
        )
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getMetrics() {
      try {
        this.loading = true
        const { data } = await api.metrics.getRules(this.organizationId)

        const organizationMetrics = data.filter(
          metric => metric.isEnabled || !!metric.id
        )
        this.impactMetrics = organizationMetrics.map(organizationMetric => {
          return {
            ...organizationMetric.metric,
            isEnabled: organizationMetric.isEnabled,
          }
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    handleImpactMetricsChange(metrics) {
      this.selectedImpacts = metrics
    },

    handleInvestmentMetricsChange(metrics) {
      this.selectedInvestments = metrics
    },

    goToValidationsAfterValidate() {
      this.$emit('skipValidation', true)
      this.navigateTo({ name: 'validations' })
    },

    handleDateChange(event, value) {
      if (event === 'changeStartDate') this.startDate = value
      else this.endDate = value

      this.trackChangeDate({
        view: 'validation',
        revId: this.revId,
        revType: this.camelCaseRevType,
        date: { startDate: this.startDate, endDate: this.endDate },
      })
    },

    startDateAllowed(date) {
      if (!this.endDate) return true

      return utils.dates.calculateDifferenceInDays(date, this.endDate) >= 0
    },

    endDateAllowed(date) {
      if (!this.startDate) return true

      return utils.dates.calculateDifferenceInDays(this.startDate, date) >= 0
    },

    goBack() {
      this.trackValidationsNavigation({
        action: 'goToAllValidations',
      })
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo({
          name: 'validations',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.half-header {
  width: 50%;
}

.sticky-options {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
.alert-container {
  background-color: $color-white !important;
}
</style>
